<template>
	<div class="card-container display-flex flex-wrap flex-column pt-5" :class="{ 'pb-4': !showPostCommentSection }">
		<div class="item display-flex flex-wrap flex-1">
			<div class="flex-col-12 px-4 display-flex flex-wrap flex-justify-space-between" @click="$emit('item-clicked')">
				<div class="flex-col-9 display-flex flex-wrap">
					<div class="flex-col-12 display-flex flex-wrap">
						<IdeaFormattedTitle :idea="idea" show-video-icon class="two-row flex-col-12" />
						<IdeaFormattedDescription :idea="idea" class="description-text flex-col-12" />
					</div>
					<div class="flex-col-12 display-flex flex-row flex-wrap flex-start--end">
						<template v-if="!isIdeaSavedToAccount">
							<div class="mr-2" v-for="tag in tags" :key="tag">
								<h5 class="tag">#{{ tag }}</h5>
							</div>
						</template>
						<IdeasCommentsCounter v-else :ideaId="ideaId" />
					</div>
				</div>
				<div class="right display-flex flex-wrap flex-justify-end">
					<div class="flex-col-12">
						<IdeaMainButton :idea="idea" focus-idea-on-save />
						<UpVotersAvatarGroup v-if="isIdeaSavedToAccount" :numToShow="5" class="mb-3" :idea="idea" />
					</div>
					<template v-if="!showPostCommentSection">
						<GetHelpBuildingIdea v-if="isIdeaSavedToAccount" class="flex-align-end" :idea="idea" />
						<TeamsLikedSpan v-else class="flex-align-end" :num-of-teams="idea.number_of_accounts_idea_is_in" :ideaId="ideaId" />
					</template>
				</div>
			</div>
			<IdeaCommentPost
				class="flex-col-12 mt-2"
				:ideaId="ideaId"
				v-if="showPostCommentSection"
				contained-mode
				@dismiss="hidePostCommentSection"
				@comment-added="hidePostCommentSection"
				ref="post_comment_input"
			/>
		</div>
	</div>
</template>

<script>
import IdeaFormattedDescription from '@/components/dialogs/IdeaDialog/IdeaFormattedDescription.vue';
import IdeaFormattedTitle from '@/components/dialogs/IdeaDialog/IdeaFormattedTitle.vue';
import { sanitizeHtml } from '@/utils/helpers';
import { useIdea, useFocusOnIdea } from '@/store/modules/inspirationCenter/getters';
import { clearFocusOnIdea } from '@/store/modules/inspirationCenter/actions';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import InspirationCenterService from './inspiration-center-service';
import IdeaMainButton from './IdeaMainButton.vue';
import TeamsLikedSpan from './TeamsLikedSpan.vue';
import { userFavoritePlyServicesIds } from '@/store/modules/auth/getters.js';
import UpVotersAvatarGroup from '@/components/InspirationCenter/UpVotersAvatarGroup.vue';
import GetHelpBuildingIdea from '@/components/dialogs/IdeaDialog/GetHelpBuildingIdea.vue';
import IdeaCommentPost from '../dialogs/IdeaDialog/IdeaCommentPost.vue';
import IdeasCommentsCounter from './IdeasCommentsCounter.vue';
export default {
	name: 'InspirationCenterMenuItem',
	props: { ideaId: { type: String } },
	components: {
		IdeaMainButton,
		TeamsLikedSpan,
		UpVotersAvatarGroup,
		GetHelpBuildingIdea,
		IdeaFormattedTitle,
		IdeaFormattedDescription,
		IdeaCommentPost,
		IdeasCommentsCounter,
	},
	computed: {
		...mapGettersShorthand({ useIdea, userFavoritePlyServicesIds, useFocusOnIdea }),
		idea() {
			return this.useIdea(this.ideaId);
		},
		tags() {
			return this.idea.tags || [];
		},
		title() {
			return this.idea.title;
		},
		description() {
			return this.idea.description;
		},
		placementServiceSlug() {
			return this.placementService?.service_slug;
		},
		placementServiceName() {
			return this.placementService?.service_name;
		},
		placementService() {
			return { service_slug: 'zendesk', service_name: 'Zendesk' };
		},
		placementServicePurposeDescription() {
			return 'your support software';
		},
		params() {
			return this.idea.params;
		},
		formattedTitle() {
			return InspirationCenterService.formatContentWithServiceDetails(this.title, this.params, this.userFavoritePlyServicesIds);
		},
		formattedDescription() {
			return InspirationCenterService.formatContentWithServiceDetails(this.description, this.params, this.userFavoritePlyServicesIds);
		},
		isIdeaSavedToAccount() {
			return InspirationCenterService.isIdeaSavedToAccount(this.idea);
		},
		showPostCommentSection() {
			return this.useFocusOnIdea === this.ideaId;
		},
	},
	methods: {
		...mapActionsShorthand({ clearFocusOnIdea }),
		getCleanHtml(dirtyContent) {
			return sanitizeHtml(dirtyContent);
		},
		hidePostCommentSection() {
			this.clearFocusOnIdea();
		},
	},
	watch: {
		showPostCommentSection: {
			handler(newV) {
				if (newV) {
					this.$nextTick(() => {
						this.$refs.post_comment_input.focus();
					});
				}
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.card-container {
	cursor: pointer;
	transition: 0.3s;
	min-height: 140px;
	border-radius: 10px;
	background: var(--white);
	border: 1px solid var(--tertiary-border-color);
	&:hover {
		box-shadow: 0px 3px 6px var(--shadow-color);
	}
	.item {
		.right {
			width: 120px;
		}
	}
}

.description-text {
	font-size: 13px;
	max-height: 34px;
}
.tag {
	color: #29282977;
}
.liked {
	color: #98a2b7;
}

::v-deep {
	.get-help {
		font-size: 9px;
	}
	.liked {
		font-size: 11px;
	}
	.description-text {
		overflow: hidden;
		display: -webkit-box;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2; // num of rows to break
	}
}
</style>
